// Single responsibility: route dashboard search result selection into side effects.
import { useCallback, useRef } from "react";

import { RESULT_TYPES, SEARCH_SELECTION_GUARD_MS } from "./searchConstants";
import { breadcrumbs } from "@/lib/logger/sentryBreadcrumb";
import { EVENTS } from "@/lib/analytics/trackEvent";
import { safeTrack } from "@/lib/safeTrack";

export function useDashboardSearchActions({
  results,
  selectedIndex,
  setSelectedIndex,
  onClose,
  navigate,
  onFilterChange,
  onTabChange,
  onQueryChange,
}) {
  const lastSelectionTimestampRef = useRef(0);

  const handleSelectResult = useCallback(
    (result) => {
      if (!result) return;

      const now = Date.now();
      if (now - lastSelectionTimestampRef.current < SEARCH_SELECTION_GUARD_MS) return;
      lastSelectionTimestampRef.current = now;

      breadcrumbs.searchResultSelected(String(result.label || ""), String(result.type || ""));
      safeTrack(
        async () => {
          await fetch("/api/analytics/track", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              eventName: EVENTS.SEARCH_RESULT_SELECTED,
              properties: {
                resultCount: Array.isArray(results) ? results.length : 0,
                selectedLabel: result?.label,
                selectedType: result?.type,
              },
            }),
            keepalive: true,
          });
        },
        "dashboard_search_result_selected",
      );

      if (result.type === RESULT_TYPES.INVENTORY) {
        onTabChange?.("dashboard");
        const selectedLine = result.originalItem;
        if (selectedLine?.status === "item_expired") onFilterChange?.("expired");
        else if (selectedLine?.status === "item_moved") onFilterChange?.("moved");
        else onFilterChange?.("all");
        const prefilledSearchText = [selectedLine?.name, selectedLine?.dosage].filter(Boolean).join(" ").trim();
        if (prefilledSearchText) onQueryChange?.(prefilledSearchText);
        onClose?.();
        return;
      }

      if (result.type === RESULT_TYPES.ACTION) {
        const action = result.originalItem?.action;
        if (typeof action === "function") action({ navigate });
        onClose?.();
      }
    },
    [navigate, onClose, onFilterChange, onQueryChange, onTabChange, results],
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (!results.length && event.key !== "Escape") return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedIndex((previousIndex) => (previousIndex + 1) % Math.max(results.length, 1));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((previousIndex) => (previousIndex <= 0 ? results.length - 1 : previousIndex - 1));
        return;
      }
      if (event.key === "Enter") {
        if (selectedIndex < 0 || selectedIndex >= results.length) return;
        event.preventDefault();
        handleSelectResult(results[selectedIndex]);
        return;
      }
      if (event.key === "Escape") {
        onClose?.();
      }
    },
    [handleSelectResult, onClose, results, selectedIndex, setSelectedIndex],
  );

  return { handleSelectResult, handleKeyDown };
}
